import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { RichText } from "../components/RichText";
import { groupByHeading } from "../lib/richtext";
import { SectionHeading, SectionShell, type SectionProps } from "./SectionRenderer";

type DocNode = {
  type?: string;
  attrs?: Record<string, unknown>;
};

/**
 * A heading and the copy beneath it, plus what the heading tells us about how
 * the passage wants to be laid out.
 *
 * `level` is null for anything before the first heading. `isLabel` marks a
 * heading with nothing under it — "Core Pillars" ahead of the three pillars —
 * which reads as a title for what follows rather than a passage of its own.
 */
interface Group {
  nodes: DocNode[];
  level: number | null;
  isLabel: boolean;
}

/**
 * Consecutive passages that sit at the same heading level are siblings, so they
 * are laid out as a row rather than a stack. A run of one is just a passage.
 */
function toRuns(groups: Group[]): Group[][] {
  const runs: Group[][] = [];

  for (const group of groups) {
    const current = runs[runs.length - 1];
    const previous = current?.[current.length - 1];
    const continues =
      current &&
      previous &&
      !previous.isLabel &&
      !group.isLabel &&
      group.level !== null &&
      group.level === previous.level;

    if (continues) current.push(group);
    else runs.push([group]);
  }

  return runs;
}

/**
 * Grid for a row of siblings: threes where they divide, otherwise pairs.
 *
 * The gutter grows with the column count. Three short passages set a single
 * gap apart — the pillars on the About page — read as one continuous block of
 * text; the extra air is what tells the eye they are three separate ideas.
 */
function gridFor(count: number): string {
  const pairs = "md:grid-cols-2 md:gap-x-16";
  if (count === 2) return pairs;
  if (count % 3 === 0 || count % 2 === 1) return `${pairs} lg:grid-cols-3 lg:gap-x-20`;
  return pairs;
}

export default function RichTextBlock({
  content,
  settings,
}: SectionProps<BlockContentMap["richText"]>) {
  const nodes = (content.body?.content ?? []) as DocNode[];

  /*
    A narrow section is narrow on purpose — legal copy, a story told in order —
    so it keeps the single readable column. A section an editor has widened to
    `standard` or beyond is asking for the horizontal room to be used, and a
    42rem column of text stranded in the middle of it wastes the width they
    asked for.
  */
  const spread = settings.containerWidth !== "narrow" && nodes.length > 0;

  if (!spread) {
    return (
      <SectionShell settings={settings}>
        <SectionHeading heading={content.heading} settings={settings} />
        {/* Constrained to a readable measure regardless of container width: long
            prose set across a full-width container is genuinely harder to read. */}
        <div className="mx-auto max-w-2xl">
          {/*
            The section heading stays still, as it does in every other block —
            it is the label on the section, not part of what arrives inside it.
          */}
          <RichText doc={content.body} animation={settings.contentAnimation} />
        </div>
      </SectionShell>
    );
  }

  const groups: Group[] = groupByHeading(nodes).map((group) => {
    const [first] = group;
    const isHeading = first?.type === "heading";
    return {
      nodes: group,
      level: isHeading ? Number(first?.attrs?.level ?? 2) : null,
      isLabel: isHeading && group.length === 1,
    };
  });

  const runs = toRuns(groups);
  const centered = settings.alignment === "center";

  return (
    <SectionShell settings={settings}>
      <SectionHeading heading={content.heading} settings={settings} />
      {runs.map((run, index) => {
        const previous = runs[index - 1];
        // A label introduces the row under it, so it sits closer to it than two
        // unrelated passages would.
        const spacing = index === 0 ? "" : previous?.[0]?.isLabel ? "mt-8" : "mt-14";

        if (run.length === 1) {
          const [group] = run;
          return (
            <div
              key={index}
              className={cn(
                spacing,
                // A lone passage still gets a measure — wide enough to use the
                // room, short enough that the eye finds the next line.
                !group.isLabel && "max-w-3xl",
                centered && "mx-auto",
              )}
            >
              <RichText
                doc={{ type: "doc", content: group.nodes }}
                animation={settings.contentAnimation}
              />
            </div>
          );
        }

        /*
          Exactly three siblings across one row get a connector rail: a hairline
          running the width of the row with a node above each passage, so the
          three read as points on one idea rather than three loose columns.

          Only at `lg`, and only for three. Below that the row wraps into two
          columns and a stray orphan, and a rail drawn across the top of a
          wrapped grid connects things that are no longer side by side.
        */
        const railed = run.length === 3;

        return (
          <div key={index} className={cn("relative", spacing)}>
            {railed && (
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-0 hidden h-px bg-gold-500/40 lg:block"
              />
            )}
            <div
              className={cn(
                "grid items-start gap-x-12 gap-y-14",
                gridFor(run.length),
                railed && "lg:pt-10",
              )}
            >
              {run.map((group, groupIndex) => (
                <div key={groupIndex} className="relative">
                  {railed && (
                    <span
                      aria-hidden="true"
                      // Centred on the rail: the column starts 2.5rem below it,
                      // so -top-10 puts the node's own centre on the line.
                      className="absolute -top-10 left-0 hidden size-2.5 -translate-y-1/2 rounded-full bg-gold-500 lg:block"
                    />
                  )}
                  <RichText
                    doc={{ type: "doc", content: group.nodes }}
                    animation={settings.contentAnimation}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </SectionShell>
  );
}
